<template>
  <div>
    <AppDocViewer ref="__AppDocViewer"></AppDocViewer>
    <vs-prompt
      :title="'Revoke access: ' + selectedApp.name"
      color="danger"
      :is-valid="removeAppNameValid"
      @accept="removeApplication()"
      @close="clearFields"
      :active.sync="showDeletePrompt"
      accept-text="Yes, Revoke"
    >
      <vs-alert active="true" color="danger"
        >You are about to revoke access to <b>{{ selectedApp.name }}</b
        >. This operation is irreversible, and can affect running automation that depends on it. To proceed enter the app name below.
      </vs-alert>
      <vs-input label="App Name" placeholder="Confirm app name" v-model="confirmAppName" class="my-2 w-full mb-4" />
    </vs-prompt>
    <vs-prompt
      v-if="!showDeletePrompt"
      :title="selectedApp.name"
      color="success"
      class="export-options"
      @accept="updateAppSettings"
      :accept-text="getActionText(selectedApp)"
      cancel-text="Cancel"
      @close="cancelAuth"
      @cancel="cancelAuth"
      :is-valid="fieldsValueValid"
      :active="showCredentialsCollector"
      :buttons-hidden="selectedApp.slug === 'gmail'"
    >
      <vs-alert active="true" color="success">
        {{ getAlertText(selectedApp) }}
        <vs-button v-if="shouldAskForCredentials" @click="showAppDoc()" class="helpButton" color="success" type="relief"
          >Need Some Help Finding Credentials?</vs-button
        >
        <vs-button v-if="selectedApp.slug === 'gmail'" color="dark" type="gradient" class="mb-5 mt-5">
          <router-link :to="getGmailPageRoute()" target="_blank" class="button-link">
            <div class="button-content">
              <span class="button-text">Please read our Google Api Service Disclosure</span>
            </div>
          </router-link>
        </vs-button>
      </vs-alert>
      <vs-divider color="warning" v-if="shouldAskForCredentials">Credentials</vs-divider>
      <vs-divider color="warning" v-if="!shouldAskForCredentials && selectedApp.active" />
      <ul>
        <li v-for="item in selectedApp.fieldsRequired" :key="item.key" :hidden="item.hidden || !item.fromUser">
          <vs-alert icon="info" active="true" color="warning">
            {{ item.description }}
          </vs-alert>
          <vs-input
            class="credentialInputField"
            :label="item.placeholder"
            color="warning"
            :placeholder="item.placeholder"
            v-model="item.value"
            :required="item.required"
          />
        </li>
      </ul>

      <vs-button v-if="selectedApp.active" @click="deletePrompt()" class="removeApplication" color="danger" type="relief">Revoke Access</vs-button>

      <vs-button
        v-if="selectedApp.slug === 'gmail'"
        @click="updateAppSettings()"
        class="removeApplication mt-5"
        type="border"
        :color="selectedApp.active ? 'success' : 'primary'"
      >
        <div class="button-content">
          <img :src="withCMSBase(selectedApp.logo.data.attributes.url)" height="20" width="20" class="mr-5" />
          <span class="button-text">{{ selectedApp.active ? 'Re-connect Gmail' : 'Connect Gmail' }}</span>
        </div>
      </vs-button>
    </vs-prompt>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import AppDocViewer from '../app-docs/AppDocViewer.vue'
import { AUTH_TYPES } from '@/constant'
import axios from 'axios'
import { getApiKey, getAutocodeCredsVerifyUrl, getBPMTsRevokeTokenUrl } from '@/utils/misc'

export default {
  name: 'CredentialsCollector',
  components: { AppDocViewer },
  data() {
    return {
      updateCallback: null,
      cancelCallback: null,
      showCredentialsCollector: false,
      selectedApp: {},
      showDeletePrompt: false,
      confirmAppName: '',
    }
  },
  computed: {
    shouldAskForCredentials() {
      return (
        this.selectedApp.fieldsRequired &&
        this.selectedApp.fieldsRequired.length > 0 &&
        this.selectedApp.fieldsRequired.every((field) => {
          return field.fromUser
        })
      )
    },
    fieldsValueValid() {
      if (!this.selectedApp || Object.keys(this.selectedApp).length === 0) return false
      return this.selectedApp.fieldsRequired.every((field) => {
        if (!field.required || !field.fromUser) return true
        return field.value != null && field.value !== ''
      })
    },
    removeAppNameValid() {
      if (this.confirmAppName === this.selectedApp.name) return true
      else return false
    },
  },
  methods: {
    getGmailPageRoute() {
      return 'google-api-services-disclosure'
    },
    deletePrompt() {
      this.showDeletePrompt = true
    },

    getActionText(app) {
      if (app.authType === AUTH_TYPES.OAUTH2) return app.active ? 'Re-Authenticate' : 'Authenticate'
      else if (app.authType === AUTH_TYPES.CREDENTIALS) return 'Update'
      else return 'Connect'
    },
    getAlertText(app) {
      if (app.authType === AUTH_TYPES.OAUTH2) {
        return app.active ? 'App is authenticated' : 'Proceed with authentication. Allow us to integrate our workflows with your account resources.'
      } else {
        return 'These credentials are important for allowing the developers to access your account resources for creating the workflows.'
      }
    },
    credentialsCollector(app, updateCallback, cancelCallback, verifyCreds = true) {
      this.selectedApp = JSON.parse(JSON.stringify(app)) // deep copy
      this.updateCallback = updateCallback
      this.cancelCallback = cancelCallback
      this.showCredentialsCollector = true
      this.verifyCreds = verifyCreds
    },
    async updateAppSettings() {
      const params = this.selectedApp.fieldsRequired.reduce((previousValue, currentValue) => {
        if (currentValue.fromUser) previousValue[currentValue.key] = currentValue.value
        return previousValue
      }, {})

      if (this.verifyCreds) {
        try {
          this.$vs.notify({
            title: 'Verifying Credentials',
            text: 'Please wait while we verify your credentials.',
            iconPack: 'feather',
            icon: 'icon-info',
          })
          await axios.post(getAutocodeCredsVerifyUrl(), { appSlug: this.selectedApp.slug, params })
        } catch (e) {
          this.$rollbar.warn(`${this.selectedApp.slug} entered credentials are not valid.`, params)
          this.$vs.notify({
            title: 'Error',
            text: 'We could not verify your credentials. Please check your credentials and try again.',
            color: 'danger',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
          })
          return
        }
      }
      await firebase
        .firestore()
        .collection('cloudify')
        .doc(JSON.parse(localStorage.getItem('userInfo')).uid)
        .set(
          {
            credentials: {
              [this.selectedApp.slug]: params,
            },
          },
          { merge: true }
        )
      if (this.updateCallback) this.updateCallback(this.selectedApp)
      this.$rollbar.info(`${this.selectedApp.slug} credentials field added`)
      this.showCredentialsCollector = false
    },
    async removeApplication() {
      this.showCredentialsCollector = false
      if (this.selectedApp.authType == AUTH_TYPES.OAUTH2) {
        await axios.get(getBPMTsRevokeTokenUrl({ apiToken: getApiKey(), appName: this.selectedApp.slug }))
      } else
        await firebase
          .firestore()
          .collection('cloudify')
          .doc(JSON.parse(localStorage.getItem('userInfo')).uid)
          .update({
            [`credentials.${this.selectedApp.slug}`]: {},
          })
    },
    showAppDoc() {
      this.$refs.__AppDocViewer.docViewerToggle = true
      this.$refs.__AppDocViewer.fetchDocFromCMS(this.selectedApp)
    },
    cancelAuth() {
      if (this.cancelCallback) this.cancelCallback()
      this.showCredentialsCollector = false
    },
    clearFields() {
      this.confirmAppName = ''
    },
  },
}
</script>

<style scoped>
.credentialInputField {
  width: 100%;
  margin-bottom: 1.5em;
}
.removeApplication {
  width: 100%;
}
.helpButton {
  margin-top: 1em;
  width: 100%;
}
.button-content {
  display: flex;
  align-items: center;
}
</style>
